import React from "react";

import { closeAllHashModals, returnToLocation } from "lib/navigation";
import { BusinessRelationshipDialog } from "components/Business/BusinessForm/Relationships/Form";
import { getBusinessRelationById } from "selectors/businessRelations";
import { useDispatch, useSelector } from "react-redux";
import { BusinessRelationAction } from "actions/businessRelation";
import { useBoolean, useHasPermission } from "hooks";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { v4 as uuidv4 } from "uuid";
import { BusinessRelationPermissions } from "constants/permissions";

interface BusinessRelationHashModalProps {
  returnTo?: string;
  relationshipId: string | null;
}

const BusinessRelationHashModal = (props: BusinessRelationHashModalProps) => {
  const { returnTo, relationshipId } = props;

  const businessRelation = useSelector(getBusinessRelationById(relationshipId));
  const hasUpdatePermissions = useHasPermission(
    getBusinessRelationById(relationshipId),
    BusinessRelationPermissions.update,
  );
  // Allow writing if it's a create (no id) or an update.
  const readOnly = !(relationshipId === undefined || hasUpdatePermissions);

  const allowDelete = useHasPermission(
    getBusinessRelationById(relationshipId),
    BusinessRelationPermissions.delete,
  );

  const dispatch = useDispatch();
  const [isConfirmDeleteOpen, openConfirmDelete, closeConfirmDelete] =
    useBoolean(false);

  const handleClose = () => {
    if (returnTo) {
      returnToLocation(returnTo);
    } else {
      closeAllHashModals();
    }
  };

  const handleSubmit = (values: object): void => {
    if (relationshipId) {
      dispatch(BusinessRelationAction.update(values));
    } else {
      dispatch(
        BusinessRelationAction.create({
          id: uuidv4(),
          ...values,
        }),
      );
    }
    handleClose();
  };

  const handleClickDelete = relationshipId
    ? () => {
        openConfirmDelete();
      }
    : null;

  const handleDelete = () => {
    dispatch(BusinessRelationAction.delete(relationshipId));
    handleClose();
  };

  const initialValues = relationshipId
    ? businessRelation
    : {
        relatedFromId: null,
        relatedToId: null,
        relationType: null,
        rateValue: null,
        rateType: null,
        saleType: null,
        saleSubTypeId: null,
        speciesId: null,
      };

  return (
    <>
      <BusinessRelationshipDialog
        isOpen
        showRelatedFromBusiness
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        handleClose={handleClose}
        handleDelete={handleClickDelete}
        allowDelete={allowDelete}
        readOnly={readOnly}
      />
      <ConfirmDialog
        title={createModalTitle("this relationship")}
        isOpen={isConfirmDeleteOpen}
        onCancel={closeConfirmDelete}
        onDelete={handleDelete}
      />
    </>
  );
};

export default BusinessRelationHashModal;
